import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useUrlState from '../../../hooks/use-url-state';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import { ISortConfig } from '../../../ts/interface/components/redesing/table.interface';
import { useParams } from 'react-router';
import { useMembershipsRelayQuery } from '../../../hooks/membership/query/V2/useMembershipsRelay';
import { useUserMembershipsRelayQuery } from '../../../hooks/membership/query/V2/useUserMembershipsRelay';
import { mapMembershipsToLegacy } from '../../../hooks/membership/query/V2/membershipMappers';
import { IFilters } from '../../../ts/interface/membership.interface';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';

import PendingMemberships from '../pending-memberships';
import PageContent from '../../../components/Redesing/page-content';
import PageContainer from '../../../components/Redesing/page-container';
import MembershipList from '../assets/membership-list';
import { changeTab } from '../../../utils';
import useOrganization from '../../../hooks/use-organization';
import { ISystem } from '../../../ts/interface/system.interface';
import { useKeysetPagination } from '../../../hooks/use-keyset-pagination';

function OrganizationMembershipPage() {
  const organizationInfo = useOrganization();

  const { currentTab = 'memberships' } = useParams<{ currentTab: string }>();
  const [currentTabV, setCurrentTabV] = useState(currentTab);
  const handleTabsChange = useCallback(
    (event: React.SyntheticEvent, value: React.SetStateAction<string>) => {
      changeTab(value, currentTab, 'memberships');
    },
    [currentTab]
  );

  const [filters, setFilters, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    status: 'active',
    selectedSystem: [] as { value: string; label: string; original: ISystem }[],
    system: '',
    sortConfig: {
      key: 'created_on',
      direction: 'asc',
    } as ISortConfig,
  });

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  const debouncedSearchTerm = useSearchDebounce(filters.search);

  useEffect(() => {
    if (currentTab !== currentTabV) {
      setCurrentTabV(currentTab);
    }
  }, [currentTab, currentTabV]);

  // Convert sort config to GraphQL format
  const sortConfig = useMemo(() => {
    const keyMap: Record<string, string> = {
      created_on: 'CREATED_ON',
      name: 'NAME',
      active_on: 'ACTIVE_ON',
      priority: 'PRIORITY',
    };
    const field = keyMap[filters.sortConfig?.key || 'created_on'] || 'CREATED_ON';
    const order =
      filters.sortConfig?.direction?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
    return [{ field: field as 'CREATED_ON' | 'NAME', order: order as 'ASC' | 'DESC' }];
  }, [filters.sortConfig]);

  // Systems selected in the multi-select filter (comma-joined gids in the URL).
  const systemIds = useMemo(
    () => (filters.system ? filters.system.split(',').filter(Boolean) : []),
    [filters.system]
  );

  const {
    data: membershipsData,
    count: totalMembershipCount,
    isLoading,
    isRefetching,
    hasMore: graphqlHasMore,
    endCursor,
  } = useMembershipsRelayQuery(
    {
      organizationId: organizationInfo?.id,
      name: debouncedSearchTerm || undefined,
      // Empty status (filter removed) lists all memberships.
      status: (filters.status?.toLowerCase() || 'all') as
        | 'active'
        | 'archived'
        | 'future'
        | 'all',
      ...(systemIds.length > 0 && { systemIds }),
    },
    {
      limit: filters.sizePerPage,
      sort: sortConfig,
      enabled: isComplete && Boolean(organizationInfo?.id),
      after,
    }
  );

  // Lightweight count-only query for the pending tab badge. The pending list
  // itself is fetched (and server-paginated) inside <PendingMemberships>.
  const { count: pendingCount } = useUserMembershipsRelayQuery(
    {
      organizationId: organizationInfo?.id,
      pending: true,
    },
    {
      limit: 1,
      enabled: Boolean(organizationInfo?.id),
    }
  );

  const pendingCountTotal = pendingCount ?? 0;

  // Render ONLY the current page (no client-side accumulation).
  const membershipsItems = useMemo(
    () => (membershipsData ? mapMembershipsToLegacy(membershipsData) : []),
    [membershipsData]
  );

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipsData]);

  const warning = useCallback(() => {
    const noActiveMemberships = membershipsItems?.length === 0;
    const noStatusFilter = !filters.status;
    const noSearchTerm = !debouncedSearchTerm;
    const hasPendingMemberships = pendingCountTotal > 0;

    if (noActiveMemberships && !isLoading && noStatusFilter && noSearchTerm) {
      return 'There are no active memberships and the system is private. Please add a membership for users to join the system.';
    }

    if (hasPendingMemberships) {
      const membershipWord = pendingCountTotal === 1 ? 'membership' : 'memberships';
      return `You have ${pendingCountTotal} pending user ${membershipWord} waiting for approval`;
    }
    return undefined;
  }, [
    debouncedSearchTerm,
    isLoading,
    membershipsItems,
    pendingCountTotal,
    filters.status,
  ]);

  return (
    <PageContainer
      isLoading={isLoading}
      warningMessage={warning()}
      spaceName={'Memberships'}
    >
      <PageContent
        detailedView={false}
        tabsList={[
          { label: 'Memberships', value: 'memberships' },
          {
            label: 'Pending Memberships',
            value: 'pending',
            warnings: pendingCountTotal,
          },
        ]}
        tabsChildren={
          <>
            {currentTabV === 'memberships' && (
              <MembershipList
                isRestoring={isRestoring}
                memberships={membershipsItems}
                isLoading={isLoading || isPaginating}
                isLoadingCache={isRefetching}
                hasMore={graphqlHasMore}
                totalItems={totalMembershipCount}
                pageNumber={pageNumber}
                filters={filters}
                setFilters={setFilters as React.Dispatch<React.SetStateAction<IFilters>>}
                isFilterComplete={isComplete}
                showSystemColumn={true}
                onSizePerPageChange={(sizePerPage) => {
                  resetPagination();
                  setFilters((prevState) => ({
                    ...prevState,
                    sizePerPage,
                  }));
                }}
                onPageChange={(newPage) => onPageChange(newPage)}
                onSearchChange={() => resetPagination()}
                onSortChange={() => resetPagination()}
                onFilterApply={() => resetPagination()}
              />
            )}
            {currentTabV === 'pending' && organizationInfo?.id && (
              <PendingMemberships scope={{ organizationId: organizationInfo.id }} />
            )}
          </>
        }
        currentTabV={currentTabV}
        handleTabsChange={handleTabsChange}
      />
    </PageContainer>
  );
}

export default OrganizationMembershipPage;
